// src/components/HabitacionCardMobile.jsx
import React, { useState, useEffect } from "react";
import { Box, Typography, Divider, Button, Stack, Chip } from "@mui/material";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import HotelIcon from "@mui/icons-material/Hotel";
import { useNavigate } from "react-router-dom";

const responsive = {
  desktop: {
    breakpoint: { max: 3000, min: 1024 },
    items: 1,
  },
  tablet: {
    breakpoint: { max: 1024, min: 464 },
    items: 1,
  },
  mobile: {
    breakpoint: { max: 464, min: 0 },
    items: 1,
  },
};

// 🌐 Origen de la API (dev + prod)
const API_ORIGIN =
  process.env.REACT_APP_API_ORIGIN ||
  (typeof window !== "undefined" && window.location.hostname === "localhost"
    ? "http://localhost:4000"
    : "https://api.ubika.site");

/**
 * Normaliza una URL de imagen:
 * - Si ya es absoluta (http/https), se devuelve igual.
 * - Si es relativa (/uploads/rooms/...), se le antepone API_ORIGIN.
 */
const resolveImageUrl = (rawUrl) => {
  if (!rawUrl || typeof rawUrl !== "string") return null;

  const url = rawUrl.trim();
  if (url === "") return null;

  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url;
  }

  if (url.startsWith("/")) {
    return `${API_ORIGIN}${url}`;
  }

  // por si se guardó sin el / inicial
  return `${API_ORIGIN}/${url}`;
};

/**
 * Helper genérico para obtener imágenes de la habitación.
 */
const getRoomImages = (room) => {
  let rawList = [];

  if (Array.isArray(room.images) && room.images.length > 0) {
    rawList = room.images;
  } else if (Array.isArray(room.imageUrls) && room.imageUrls.length > 0) {
    rawList = room.imageUrls;
  } else if (typeof room.imageUrl === "string" && room.imageUrl.trim() !== "") {
    rawList = [room.imageUrl.trim()];
  }

  const resolved = rawList
    .map((item) => {
      if (typeof item === "string") return resolveImageUrl(item);
      if (item && typeof item === "object") {
        const candidate = item.url || item.path || item.src;
        return resolveImageUrl(candidate);
      }
      return null;
    })
    .filter(Boolean);

  if (resolved.length === 0) {
    return ["https://via.placeholder.com/800x400?text=Sin+imagen+disponible"];
  }

  return resolved;
};

const HabitacionCardMobile = ({
  room,
  formatCurrency,
  calculateOffer, // viene de ReserveLayout
  startDate,
  endDate,
  numGuests,
  // 🆕 vienen propagados desde ReserveLayout → ListaHabitacionesMobile → HabitacionCardMobile
  currency, // "ARS" / "CLP"
  propertyId, // id de la propiedad
  mpPublicKey, // public key MP del cliente
  mpLocale, // "es-AR" / "es-CL" / etc.
}) => {
  const [isAvailable, setIsAvailable] = useState(false);
  const [availableUnits, setAvailableUnits] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const avail = Array.isArray(room.availability) ? room.availability : [];
    setIsAvailable(avail.length > 0);
    setAvailableUnits(avail);
  }, [room.availability]);

  // 💱 moneda efectiva de esta habitación
  const roomCurrency = (room.currency || currency || "ARS").toUpperCase();

  // 🔢 noches (por si te sirve en /pay)
  const totalNights = Math.max(
    (new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24),
    1
  );

  // 💰 Calculamos UNA sola vez la oferta total usando la misma función que en desktop
  const totalOffer = calculateOffer(room);

  const roomImages = getRoomImages(room);
  const amenities = Array.isArray(room.amenities) ? room.amenities : [];

  const handleReservar = () => {
    // 🔒 Validación para habitaciones compartidas
    if (room.type === "shared") {
      if (
        !Array.isArray(room.availability) ||
        room.availability.length < numGuests
      ) {
        alert(
          "No hay suficientes camas disponibles para la cantidad de huéspedes."
        );
        return;
      }
    }

    // 🛏️ Para privadas, seleccionamos una unidad
    const selectedUnitId =
      room.type === "private" &&
      Array.isArray(room.availability) &&
      room.availability.length > 0
        ? room.availability[0]
        : null;

    // 🛏️ Para compartidas, seleccionamos las camas necesarias
    const selectedUnitIds =
      room.type === "shared"
        ? room.availability.slice(0, numGuests)
        : [selectedUnitId];

    console.log("➡️ Navegando a /pay (mobile) con:", {
      roomId: room.roomId,
      totalOffer,
      totalNights,
      numGuests,
      currency: roomCurrency,
      propertyId,
      mpPublicKey,
      mpLocale,
    });

    navigate("/pay", {
      state: {
        roomId: room.roomId,
        name: room.name,
        unitIds: selectedUnitIds,
        type: room.type,
        pricePerGuest: room.pricePerGuest,
        totalOffer, // ✅ igual que lo mostrado
        startDate,
        endDate,
        numGuests,
        totalNights,
        // 🔑 datos para flujo de pago genérico
        currency: roomCurrency,
        propertyId,
        mpPublicKey,
        mpLocale,
      },
    });
  };

  const isReservable =
    isAvailable &&
    ((room.type === "shared" && availableUnits.length >= numGuests) ||
      (room.type === "private" && numGuests <= room.maxGuests));

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column", // 🤳 100% pensado mobile
        gap: 1.5,
        boxShadow: 3,
        borderRadius: 2,
        mb: 2,
        backgroundColor: "#ffffff",
        overflow: "hidden",
      }}
    >
      {/* 🔄 Carrusel de imágenes */}
      <Box sx={{ width: "100%" }}>
        <Carousel
          responsive={responsive}
          infinite
          autoPlay
          autoPlaySpeed={3000}
          arrows={true}
          showDots={true}
        >
          {roomImages.map((image, index) => (
            <img
              key={index}
              src={image}
              alt={`Habitación ${room.name}`}
              style={{
                width: "100%",
                height: "220px",
                objectFit: "cover",
              }}
            />
          ))}
        </Carousel>
      </Box>

      {/* 🔄 Información de la habitación */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          px: 2,
          pb: 2,
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{
              textAlign: "left",
              textTransform: "capitalize",
              mb: 1,
            }}
          >
            {room.name} · {room.type === "shared" ? "Compartida" : "Privada"}
          </Typography>

          <Stack direction="row" spacing={1} mb={1.5}>
            <Chip
              icon={<HotelIcon />}
              label={`Máx. ${room.maxGuests} ${
                room.maxGuests > 1 ? "personas" : "persona"
              }`}
              variant="outlined"
              sx={{
                color: "#2F4D48 !important",
                px: 1.2,
                borderColor: "#2F4D48 !important",
                "& .MuiSvgIcon-root": {
                  color: "#2F4D48 !important",
                },
              }}
            />
            <Chip
              label={isAvailable ? "Disponible" : "Ocupada"}
              color={isAvailable ? "success" : "error"}
              size="small"
            />
          </Stack>

          <Divider sx={{ mb: 1.5 }} />

          <Typography
            variant="body2"
            sx={{
              textAlign: "left",
              color: "red",
              textDecoration: "line-through",
              mb: 0.3,
            }}
          >
            Precio por {room.type === "private" ? "noche" : "huésped"}:{" "}
            <strong>
              {formatCurrency(room.pricePerGuest * 1.25)} {roomCurrency}
            </strong>
          </Typography>

          <Typography variant="body2" sx={{ textAlign: "left", mb: 0.8 }}>
            20% OFF. Precio por {room.type === "private" ? "noche" : "huésped"}:{" "}
            <strong>
              {formatCurrency(room.pricePerGuest)} {roomCurrency}
            </strong>
          </Typography>

          <Typography
            variant="subtitle1"
            sx={{
              textAlign: "left",
              color: "#4CAF50",
              fontWeight: "bold",
              mb: 1.2,
            }}
          >
            Total por {totalNights} noche
            {totalNights > 1 ? "s" : ""}: {formatCurrency(totalOffer)}{" "}
            {roomCurrency}
          </Typography>

          {/* 🔄 Amenities dinámicos, versión compacta mobile */}
          {amenities.length > 0 && (
            <>
              <Typography
                variant="subtitle2"
                sx={{ textAlign: "left", mb: 0.5 }}
              >
                Servicios incluidos:
              </Typography>
              <Stack
                spacing={0.2}
                sx={{ ml: 1.5, maxHeight: 72, overflow: "hidden" }}
              >
                {amenities.map((am, idx) => (
                  <Typography
                    variant="body2"
                    key={idx}
                    sx={{ fontSize: "0.8rem" }}
                  >
                    • {am}
                  </Typography>
                ))}
              </Stack>
            </>
          )}

          {room.type === "shared" && (
            <Typography
              variant="body2"
              sx={{ ml: 1.5, mt: 1, fontSize: "0.8rem" }}
            >
              🛏️ {availableUnits.length} camas disponibles
            </Typography>
          )}
        </Box>

        <Button
          onClick={handleReservar}
          variant="contained"
          color="primary"
          disabled={!isReservable}
          fullWidth
          sx={{
            mt: 2,
            backgroundColor: "#1976D2",
            "&:hover": {
              backgroundColor: "#1565C0",
            },
            opacity: isReservable ? 1 : 0.5,
            cursor: isReservable ? "pointer" : "not-allowed",
          }}
        >
          <Typography color="#fff" variant="button">
            {!isAvailable
              ? "Ocupada"
              : room.type === "shared" && availableUnits.length < numGuests
              ? "Camas insuficientes"
              : room.type === "private" && numGuests > room.maxGuests
              ? "Excede capacidad"
              : "Reservar ahora"}
          </Typography>
        </Button>
      </Box>
    </Box>
  );
};

export default HabitacionCardMobile;
